import React from 'react';
import {
  Alert,
  Box,
  Button,
  FormControlLabel,
  Paper,
  Snackbar,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import BillingService, {
  BillingNotificationPreview,
  BillingNotificationTemplate,
  NotificationChannel,
  NotificationFlow,
} from '../../../services/BillingService';

const FLOWS: Array<{ flow: NotificationFlow; title: string }> = [
  { flow: 'Case', title: 'Tahsilat' },
  { flow: 'Dealer', title: 'Bayi bildirimi' },
];

const TOKENS = ['sirket', 'bayi', 'tutar', 'gun', 'durum', 'sonTarih', 'link', 'kalemler', 'sube', 'kalem'];

const EXAMPLES: Record<NotificationChannel, Record<NotificationFlow, { subject: string; body: string }>> = {
  Email: {
    Case: {
      subject: 'AQUASOFT Yenileme Hatırlatması — {durum}',
      body: 'Sayın {sirket},\n\nHizmet süreniz: {durum}. Son tarih: {sonTarih}.\nTutar: {tutar}.\nKalemler: {kalemler}.\nKesintisiz kullanım için ödemeyi tamamlayın.',
    },
    Dealer: {
      subject: 'AQUASOFT Bayi Hatırlatması — {sirket}, {durum}',
      body: 'Sayın {bayi},\n\n{sirket} ({sube}) kaydında {kalem} için süre durumu: {durum}. Son tarih: {sonTarih}.\nFiyatlandırma ve yenileme tarafınızdadır.',
    },
  },
  Sms: {
    Case: {
      subject: '',
      body: 'Sayin {sirket}, {kalemler} yenilemeniz ({durum}). Tutar: {tutar}. Son tarih: {sonTarih}. Odeme: {link}',
    },
    Dealer: {
      subject: '',
      body: 'Sayin {bayi}, {sirket} ({sube}) {kalem}: {durum}. Son tarih: {sonTarih}. Yenileme tarafınızdadır.',
    },
  },
};

const emptyPreview: BillingNotificationPreview = {
  subject: '',
  html: '',
  sms: '',
  charCount: 0,
  segments: 0,
  unicode: false,
};

const TemplatesTab: React.FC = () => {
  const [rows, setRows] = React.useState<BillingNotificationTemplate[]>([]);
  const [channel, setChannel] = React.useState<NotificationChannel>('Email');
  const [flow, setFlow] = React.useState<NotificationFlow>('Case');
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [toast, setToast] = React.useState<string | null>(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(await BillingService.getNotificationTemplates());
    } catch (e: any) {
      setError(e?.message || 'Şablonlar yüklenemedi.');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    load();
  }, [load]);

  const row = rows.find((item) => item.flow === flow && item.channel === channel) ?? null;

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Tabs
        value={channel}
        onChange={(_, value: NotificationChannel) => setChannel(value)}
        sx={{
          mb: 1,
          minHeight: 40,
          borderBottom: 1,
          borderColor: 'divider',
          '& .MuiTab-root': { textTransform: 'none', minHeight: 40, fontWeight: 600 },
        }}
      >
        <Tab value="Email" label="E-posta" />
        <Tab value="Sms" label="SMS" />
      </Tabs>
      <Tabs
        value={flow}
        onChange={(_, value: NotificationFlow) => setFlow(value)}
        sx={{
          mb: 2,
          minHeight: 40,
          '& .MuiTab-root': { textTransform: 'none', minHeight: 40, fontWeight: 600 },
        }}
      >
        {FLOWS.map((item) => (
          <Tab key={item.flow} value={item.flow} label={item.title} />
        ))}
      </Tabs>
      <TemplateFields
        key={`${channel}-${flow}`}
        flow={flow}
        channel={channel}
        row={row}
        loading={loading}
        onSaved={(saved) => {
          setRows((prev) => [
            ...prev.filter((item) => !(item.flow === saved.flow && item.channel === saved.channel)),
            saved,
          ]);
          setToast('Kaydedildi.');
        }}
        onError={setError}
      />
      <Snackbar open={!!toast} autoHideDuration={2500} onClose={() => setToast(null)} message={toast || ''} />
    </Box>
  );
};

interface FieldsProps {
  flow: NotificationFlow;
  channel: NotificationChannel;
  row: BillingNotificationTemplate | null;
  loading: boolean;
  onSaved: (row: BillingNotificationTemplate) => void;
  onError: (message: string) => void;
}

const TemplateFields: React.FC<FieldsProps> = ({ flow, channel, row, loading, onSaved, onError }) => {
  const example = EXAMPLES[channel][flow];
  const [subject, setSubject] = React.useState(row?.subject || example.subject);
  const [body, setBody] = React.useState(row?.body || example.body);
  const [enabled, setEnabled] = React.useState(row?.isEnabled ?? true);
  const [saving, setSaving] = React.useState(false);
  const [preview, setPreview] = React.useState<BillingNotificationPreview>(emptyPreview);
  const bodyRef = React.useRef<HTMLTextAreaElement | null>(null);

  React.useEffect(() => {
    setSubject(row?.subject || example.subject);
    setBody(row?.body || example.body);
    setEnabled(row?.isEnabled ?? true);
  }, [row, example.subject, example.body]);

  React.useEffect(() => {
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      try {
        const next = await BillingService.previewNotificationTemplate({ flow, channel, subject, body });
        if (!cancelled) setPreview(next);
      } catch {
        if (!cancelled) setPreview(emptyPreview);
      }
    }, 350);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [flow, channel, subject, body]);

  const insertToken = (token: string) => {
    const snippet = `{${token}}`;
    const el = bodyRef.current;
    if (!el) {
      setBody((prev) => prev + snippet);
      return;
    }
    const start = el.selectionStart ?? body.length;
    const end = el.selectionEnd ?? body.length;
    setBody(body.slice(0, start) + snippet + body.slice(end));
    window.requestAnimationFrame(() => {
      el.focus();
      const caret = start + snippet.length;
      el.setSelectionRange(caret, caret);
    });
  };

  const save = async () => {
    if (!body.trim()) {
      onError('Metin zorunludur.');
      return;
    }
    setSaving(true);
    try {
      const saved = await BillingService.saveNotificationTemplate({
        flow,
        channel,
        subject: channel === 'Email' ? subject : null,
        body: body.trim(),
        isEnabled: enabled,
      });
      onSaved(saved);
    } catch (e: any) {
      onError(e?.message || 'Kaydedilemedi.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        gap: 2,
        alignItems: 'stretch',
      }}
    >
      <Box sx={{ flex: '1 1 42%', minWidth: 0 }}>
        {channel === 'Email' && (
          <TextField
            label="Konu"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            fullWidth
            size="small"
            disabled={loading}
            sx={{ mb: 1.5 }}
          />
        )}
        <TextField
          label="Metin"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          fullWidth
          size="small"
          multiline
          minRows={channel === 'Sms' ? 8 : 12}
          disabled={loading}
          inputRef={bodyRef}
        />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          {TOKENS.map((token, i) => (
            <React.Fragment key={token}>
              {i > 0 ? '  ' : ''}
              <Box
                component="span"
                onClick={() => insertToken(token)}
                sx={{ cursor: 'pointer', color: 'primary.main' }}
              >
                {`{${token}}`}
              </Box>
            </React.Fragment>
          ))}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1.5, gap: 1 }}>
          <FormControlLabel
            sx={{ mr: 0 }}
            control={<Switch size="small" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />}
            label={enabled ? 'Açık' : 'Kapalı'}
          />
          <Button variant="contained" onClick={save} disabled={saving || loading} sx={{ textTransform: 'none' }}>
            {saving ? 'Kaydediliyor…' : 'Kaydet'}
          </Button>
        </Box>
      </Box>

      <Box sx={{ flex: '1 1 58%', minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1, mb: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Önizleme
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Örnek Market · 1.250,00 TRY · 7 gün kaldı
          </Typography>
        </Box>
        {channel === 'Email' ? (
          <>
            <Typography variant="body2" sx={{ fontWeight: 700, mb: 1 }}>
              {preview.subject || 'Konu'}
            </Typography>
            <Box
              component="iframe"
              title="E-posta önizleme"
              sandbox=""
              srcDoc={preview.html || '<p></p>'}
              sx={{
                width: '100%',
                flex: 1,
                minHeight: { xs: 420, md: 'calc(100vh - 280px)' },
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                bgcolor: '#f6f1e8',
              }}
            />
          </>
        ) : (
          <>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                flex: 1,
                minHeight: { xs: 180, md: 280 },
                bgcolor: '#e8f5e9',
                borderRadius: 2,
                whiteSpace: 'pre-wrap',
              }}
            >
              {preview.sms || body || 'SMS metni burada görünür.'}
            </Paper>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              {preview.charCount || body.length} karakter
              {preview.segments ? ` · ${preview.segments} SMS` : ''}
              {preview.unicode ? ' · Unicode' : ''}
              {!enabled ? ' · kapalıyken SMS gitmez' : ''}
            </Typography>
          </>
        )}
      </Box>
    </Box>
  );
};

export default TemplatesTab;
